import React, { useEffect, useState } from 'react';
import { TbBrandGithub, TbBrandLinkedin, TbMail } from 'react-icons/tb';
import './Header.css';
import { Link, scroller } from 'react-scroll';
import { motion, AnimatePresence } from 'framer-motion';

const NAV_LINKS = ['home', 'work', 'services', 'about', 'skills', 'contact'];

const Header = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    scroller.scrollTo('home', { duration: 0, delay: 0, smooth: false });
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      if (window.scrollY > 60 && menuOpen) setMenuOpen(false);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [menuOpen]);

  const handleLinkClick = () => setMenuOpen(false);

  return (
    <>
      <a href="#home" className="skip-nav">Skip to content</a>
      <div id="header">
        <motion.nav
          className={`navbar navbar-dark${scrolled ? ' scrolled' : ''}`}
          aria-label="Main navigation"
          initial={{ y: -90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
        >
          {/* Logo */}
          <Link
            className="navbar-brand logo-text"
            to="home"
            href="/#home"
            smooth
            duration={500}
            aria-label="Obaidullah Zeeshan – Home"
          >
            OZ
          </Link>

          {/* Nav links – desktop */}
          <ul className="navbar-nav nav-center">
            {NAV_LINKS.map(section => (
              <li className="nav-item" key={section}>
                <Link
                  className={`nav-link${activeSection === section ? ' active' : ''}`}
                  to={section}
                  href={`/#${section}`}
                  spy
                  smooth
                  duration={500}
                  onSetActive={() => setActiveSection(section)}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                  {activeSection === section && (
                    <motion.span
                      className="nav-active-pill"
                      layoutId="nav-pill"
                      layout="position"
                      transition={{ type: 'spring', stiffness: 520, damping: 32 }}
                    />
                  )}
                </Link>
              </li>
            ))}
          </ul>

          {/* Right – socials + CTA + hamburger */}
          <div className="nav-right">
            <motion.a
              className="social-link"
              href="https://github.com/ObaidGits"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub profile"
              whileHover={{ y: -2, scale: 1.15 }}
              transition={{ duration: 0.2 }}
            >
              <TbBrandGithub />
            </motion.a>
            <motion.a
              className="social-link"
              href="https://www.linkedin.com/in/obaidullah-zeeshan/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn profile"
              whileHover={{ y: -2, scale: 1.15 }}
              transition={{ duration: 0.2 }}
            >
              <TbBrandLinkedin />
            </motion.a>
            <motion.a
              className="social-link"
              href="mailto:obaidzeeshan.official@gmail.com"
              aria-label="Send an email"
              whileHover={{ y: -2, scale: 1.15 }}
              transition={{ duration: 0.2 }}
            >
              <TbMail />
            </motion.a>

            <Link className="header-cta" to="contact" href="/#contact" smooth duration={600} offset={-80}>
              Let's Talk
            </Link>

            {/* Animated hamburger */}
            <button
              className={`hamburger${menuOpen ? ' open' : ''}`}
              onClick={() => setMenuOpen(v => !v)}
              aria-label="Toggle navigation"
              aria-expanded={menuOpen}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </motion.nav>

        {/* Mobile menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              className="mobile-menu"
              initial={{ opacity: 0, y: -14, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -14, scale: 0.97 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              <ul>
                {NAV_LINKS.map((section, i) => (
                  <motion.li
                    key={section}
                    initial={{ opacity: 0, x: -18 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.055, duration: 0.22 }}
                  >
                    <Link
                      className={`mobile-nav-link${activeSection === section ? ' active' : ''}`}
                      to={section}
                      href={`/#${section}`}
                      spy
                      smooth
                      duration={500}
                      onSetActive={() => setActiveSection(section)}
                      onClick={handleLinkClick}
                    >
                      {section.charAt(0).toUpperCase() + section.slice(1)}
                    </Link>
                  </motion.li>
                ))}
              </ul>
              <div className="mobile-footer">
                <div className="mobile-socials">
                  <a href="https://github.com/ObaidGits" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                    <TbBrandGithub />
                  </a>
                  <a href="https://www.linkedin.com/in/obaidullah-zeeshan/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                    <TbBrandLinkedin />
                  </a>
                  <a href="mailto:obaidzeeshan.official@gmail.com" aria-label="Send an email">
                    <TbMail />
                  </a>
                </div>
                <Link className="mobile-cta" to="contact" href="/#contact" smooth duration={600} onClick={handleLinkClick}>
                  Let's Talk
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default Header;